import { ReactComponent as CircleXMarkSolid } from "../../assets/icons/circle-x-mark-solid.svg";
import ToggleSwitchComponent from "../../atoms/toggle-switch-component";
import useReceiptCustomisationStore from "../../stores/receipt-customisation/receipt-customisation-store";
import NumberStepInputComponent from "./number-step-input-component";

const AddImageComponent = ({
  imageSrc,
  imageInputId,
  onImageSubmit,
  onImageClear,
}: {
  imageSrc?: string;
  imageInputId: string;
  onImageSubmit?: (image: File) => void;
  onImageClear: () => void;
}) => {
  const onUploadClick = () => {
    document.getElementById(imageInputId)?.click();
  };

  const onImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      onImageSubmit?.(e.target.files[0]);
    }
  };

  if (imageSrc) {
    return (
      <div
        className="flex justify-center"
        style={{
          width: 115,
          height: 40,
        }}
      >
        <div className="relative">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={imageSrc}
            alt="header_image"
            style={{
              maxHeight: "100%",
              maxWidth: "100%",
            }}
          />
          <CircleXMarkSolid
            className="absolute w-12 h-12 text-darwin-neutral-200 -top-5 -right-10 cursor-pointer"
            onClick={onImageClear}
          />
        </div>
      </div>
    );
  }

  return (
    // Body/Body 1/Regular
    <div
      className="px-24 py-8 text-14 font-400 leading-6 bg-darwin-neutral-50 rounded-4 text-darwin-neutral-1000 cursor-pointer"
      onClick={onUploadClick}
    >
      Add Image
      <input
        type="file"
        accept="image/*"
        id={imageInputId}
        onChange={onImageUpload}
        hidden
      />
    </div>
  );
};

const MeasurementComponent = ({
  imageUrl,
  title,
  toggleOn,
  heightValue,
  onToggleChange,
  onHeightChange,
  onFileUpload,
  onImageClear,
  customFields = [],
}: {
  imageUrl?: string;
  title: string;
  toggleOn: boolean;
  heightValue: number;
  onToggleChange: (toggleState: boolean) => void;
  onHeightChange: (heightValue: number) => void;
  onFileUpload: ({
    fileUrl,
    fileObject,
  }: {
    fileUrl: string;
    fileObject: File;
  }) => void;
  onImageClear: () => void;
  customFields?: {
    fieldType: "toogle";
    value: boolean;
    onValueChange: (val: boolean) => void;
    label: string;
  }[];
}) => {
  const handleOnImageSubmit = (image: File) => {
    const fileUrl = URL.createObjectURL(image);

    onFileUpload({
      fileUrl,
      fileObject: image,
    });
  };

  return (
    <div className="px-16">
      <div className="flex justify-between items-center border-b border-darwin-neutral-100 pb-8">
        {/* Body/Body 1/Bold */}
        <div className="text-14 font-700 leading-6">{title}</div>
        <ToggleSwitchComponent
          toggleId={title}
          isOn={toggleOn}
          handleSwitchChange={() => onToggleChange(!toggleOn)}
        />
      </div>
      {toggleOn ? (
        <div className="space-y-8 py-16">
          <div className="flex justify-between items-center">
            {/* Body/Body 1/Regular */}
            <div className="text-14 font-400 leading-6">{`${title} Image`}</div>
            <AddImageComponent
              imageSrc={imageUrl}
              onImageSubmit={handleOnImageSubmit}
              imageInputId={`${title}_image_id`}
              onImageClear={onImageClear}
            />
          </div>
          <div className="flex justify-between items-center">
            {/* Body/Body 1/Regular */}
            <div className="text-14 font-400 leading-6">{`${title} Height`}</div>
            <NumberStepInputComponent
              value={heightValue}
              onValueChange={(e) => {
                onHeightChange(Number(e.target.value));
              }}
              suffix="cm"
            />
          </div>
          {customFields.map((item) => (
            <div className="flex justify-between items-center">
              {/* Body/Body 1/Regular */}
              <div className="text-14 font-400 leading-6">{item.label}</div>
              <ToggleSwitchComponent
                toggleId={item.label}
                isOn={item.value}
                handleSwitchChange={() => item.onValueChange(!item.value)}
              />
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
};

const HeaderAndFooter = () => {
  const { header, setHeader, footer, setFooter } =
    useReceiptCustomisationStore();
  const { showHeader, headerHeight, headerImageSrc } = header;
  const { showFooter, footerHeight, footerImageSrc } = footer;

  const handleHeaderToggle = (newToggleState: boolean) => {
    setHeader({ ...header, showHeader: newToggleState });
  };

  const handleHeaderImageUpload = ({
    fileUrl,
    fileObject,
  }: {
    fileUrl: string;
    fileObject: File;
  }) => {
    setHeader({
      ...header,
      headerImageFile: fileObject,
      headerImageSrc: fileUrl,
    });
  };

  const handleHeaderImageHeight = (height: number) => {
    setHeader({
      ...header,
      headerHeight: height,
    });
  };

  const handleFooterToggle = (newToggleState: boolean) => {
    setFooter({
      ...footer,
      showFooter: newToggleState,
    });
  };

  const handleFooterImageUpload = ({
    fileUrl,
    fileObject,
  }: {
    fileUrl: string;
    fileObject: File;
  }) => {
    setFooter({
      ...footer,
      footerImageFile: fileObject,
      footerImageSrc: fileUrl,
    });
  };

  const handleFooterImageHeight = (height: number) => {
    setFooter({
      ...footer,
      footerHeight: height,
    });
  };

  const handleHeaderClear = () => {
    setHeader({
      ...header,
      headerImageSrc: undefined,
      headerImageFile: undefined,
    });
  };

  const handleFooterClear = () => {
    setFooter({
      ...footer,
      footerImageSrc: undefined,
      footerImageFile: undefined,
    });
  };

  return (
    <div className="py-16 space-y-8">
      <MeasurementComponent
        title="Header"
        imageUrl={headerImageSrc}
        toggleOn={showHeader}
        heightValue={Number(headerHeight)}
        onToggleChange={handleHeaderToggle}
        onFileUpload={handleHeaderImageUpload}
        onHeightChange={handleHeaderImageHeight}
        onImageClear={handleHeaderClear}
      />
      <MeasurementComponent
        title="Footer"
        imageUrl={footerImageSrc}
        toggleOn={showFooter}
        heightValue={Number(footerHeight)}
        onToggleChange={handleFooterToggle}
        onFileUpload={handleFooterImageUpload}
        onHeightChange={handleFooterImageHeight}
        onImageClear={handleFooterClear}
        customFields={[
          {
            label: "Payment ID",
            value: !!footer.showPaymentId,
            onValueChange: (showPaymentId) =>
              setFooter({ ...footer, showPaymentId }),
            fieldType: "toogle",
          },
        ]}
      />
    </div>
  );
};

export default HeaderAndFooter;
